<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'yieldManagement' }">{{ t('back') }}</ButtonBack>
  </ButtonBackContainer>

  <section class="carvia-container">
    <EditYieldManagementKmForm v-if="settingsDto" :settings-dto="settingsDto" @save="handleSave" />
  </section>
</template>

<script lang="ts" setup>
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import {
  useUpdateYieldManagementSettings,
  useYieldManagementSettings,
} from '@/queries/use-yield-management-settings.ts';
import EditYieldManagementKmForm from '@/views/yield-management/yield-management-settings/EditYieldManagementKmForm.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import { computed, watchEffect } from 'vue';
import { useUiStore } from '@/stores/ui.store.ts';
import { useI18n } from 'vue-i18n';
import { useMoneyClicks } from '@/hooks/use-money-clicks.ts';
import type { UpdateYieldManagementSettingsDto } from '@carvia/ros-client-types';
import { Alert } from '@/services/alert.service';

const { t } = useI18n();
const uiStore = useUiStore();

watchEffect(() => {
  uiStore.setHeaderTitle(t('kmRelatedStuff'));
});

const { data: settings } = useYieldManagementSettings();

const settingsDto = computed<UpdateYieldManagementSettingsDto | undefined>(() =>
  settings.value
    ? {
        extraKmCategoryFactors: settings.value.extraKmCategoryFactors,
        pricePerExtraKmSteps: settings.value.pricePerExtraKmSteps,
        inclusiveKms: settings.value.inclusiveKms,
      }
    : undefined,
);

const { mutateAsync: updateSettings } = useUpdateYieldManagementSettings();

const handleSave = async (values: UpdateYieldManagementSettingsDto) => {
  const { isConfirmed } = await Alert.fire({
    text: t('reallySaveChanges'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('saveChanges'),
    denyButtonText: t('cancel'),
  });
  if (!isConfirmed) {
    return;
  }
  await updateSettings(values);
};

useMoneyClicks();
</script>

<i18n lang="json">
{
  "en": {
    "kmRelatedStuff": "Km related 🤑💸💶💰💵 stuff",
    "reallySaveChanges": "Do you really want to save? This will change prices and kilometers for all bookings from now on.",
    "saveChanges": "Save changes"
  },
  "de": {
    "kmRelatedStuff": "KM-bezogene 🤑💸💶💰💵 Sachen",
    "reallySaveChanges": "Möchtest du wirklich speichern? Dies wird die Preise und Kilometer für alle Buchungen ab jetzt ändern.",
    "saveChanges": "Änderungen speichern"
  },
  "es": {
    "kmRelatedStuff": "Cosas de los km 🤑💸💶💰💵",
    "reallySaveChanges": "¿Seguro que quieres guardar? Cambiará precios y kilómetros para todas las reservas a partir de ahora.",
    "saveChanges": "Guardar cambios"
  }
}
</i18n>
